import * as Yup from 'yup';
import * as React from 'react';
import { Grid, Skeleton } from '@mui/material';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';

import {
	CerebroPageContainer,
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroForm,
	CerebroDrawer,
	CerebroTextButton,
	CerebroStyledBox,
	CerebroRichText,
} from '../shared/components';
import { QueryKeys, useDealQuery, useNavigateToNextPage } from '../shared/hooks';
import { useApi, useAuth } from '../shared/context';
import { Deal, UpdateDealDto } from '../shared/models';
import { FormikUtils } from '../shared/utils';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { getApiResponseData } from '../shared/utils/axios';

export const CompanyBusinessAndPrincipal: React.FC = () => {
	const [isBusinessDetailsExampleOpen, setIsBusinessDetailsExampleOpen] = React.useState(false);
	const [isPrincipalDetailsExampleOpen, setIsPrincipalDetailsExampleOpen] = React.useState(false);
	const [isBusinessOwnershipNarrativeExampleOpen, setBusinessOwnershipNarrativeExampleOpen] = React.useState(false);

	const snackbar = useSnackbar();

	const api = useApi();
	const { currentUser } = useAuth();

	const navigateToNextPage = useNavigateToNextPage();

	const { guid } = useParams<'guid'>();

	const queryClient = useQueryClient();

	const dealQuery = useDealQuery(guid);

	const dealMutation = useMutation(
		async ({ values }: DealMutationVariables) => {
			const dealPatch: UpdateDealDto = { ...values };

			const { data, error } = await getApiResponseData<Deal>(
				api.patch(CerebroApiEndpoints.Deals.Root(guid), dealPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: deal => {
				queryClient.setQueryData(QueryKeys.Deal.Root(guid), deal);
				snackbar.enqueueSnackbar('Progress Saved.', { variant: 'success' });
				navigateToNextPage();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<>
			<CerebroPageContainer>
				<CerebroBreadcrumb>Company &gt; Narrative</CerebroBreadcrumb>
				<CerebroHeading>Tell us the story of your business.</CerebroHeading>
				<CerebroText sx={{ marginTop: 3, marginBottom: 6 }}>
					Use this to provide a descriptive narrative about your business, principal, and reasons for
					financing.
				</CerebroText>
				<Formik
					validationSchema={CompanyBusinessAndPrincipalSchema}
					enableReinitialize
					onSubmit={FormikUtils.MutateOnSubmit(dealMutation.mutate)}
					initialValues={{
						narrative: dealQuery.data?.narrative ?? '',
						principalDetails: dealQuery.data?.principalDetails ?? '',
						businessOwnershipNarrative: dealQuery.data?.businessOwnershipNarrative ?? '',
						companyNotes: dealQuery.data?.companyNotes ?? '',
					}}
				>
					{({ isSubmitting, values, setFieldValue, handleSubmit }) => (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid container justifyContent="space-between" flexWrap="nowrap" sx={{ marginTop: 3 }}>
								<Grid item>
									<CerebroLabel>
										What is the company business model (how do you generate revenue)?
									</CerebroLabel>
								</Grid>

								<Grid item>
									<CerebroTextButton
										size="small"
										onClick={() => setIsPrincipalDetailsExampleOpen(true)}
									>
										View Example
									</CerebroTextButton>
								</Grid>
							</Grid>
							{dealQuery.isSuccess ? (
								<CerebroRichText
									value={values.principalDetails ?? ''}
									onChange={updatedValue => setFieldValue('principalDetails', updatedValue)}
								/>
							) : (
								<Skeleton height={60} />
							)}

							<Grid container justifyContent="space-between" flexWrap="nowrap">
								<Grid item>
									<CerebroLabel>
										What is the purpose of your financing? What is important to you about how the
										loan is structured?
									</CerebroLabel>
								</Grid>
								<Grid item>
									<CerebroTextButton
										size="small"
										onClick={() => setIsBusinessDetailsExampleOpen(true)}
									>
										View Example
									</CerebroTextButton>
								</Grid>
							</Grid>
							{dealQuery.isSuccess ? (
								<CerebroRichText
									value={values.narrative ?? ''}
									onChange={updatedValue => setFieldValue('narrative', updatedValue)}
								/>
							) : (
								<Skeleton height={60} />
							)}

							<Grid container justifyContent="space-between" flexWrap="nowrap">
								<Grid item>
									<CerebroLabel>What is the ownership structure?</CerebroLabel>
								</Grid>
								<Grid item>
									<CerebroTextButton
										size="small"
										onClick={() => setBusinessOwnershipNarrativeExampleOpen(true)}
									>
										View Example
									</CerebroTextButton>
								</Grid>
							</Grid>
							{dealQuery.isSuccess ? (
								<CerebroRichText
									value={values.businessOwnershipNarrative ?? ''}
									onChange={updatedValue => setFieldValue('businessOwnershipNarrative', updatedValue)}
								/>
							) : (
								<Skeleton height={60} />
							)}

							{currentUser?.isEmployeeUser === true && (
								<>
									<CerebroLabel htmlFor="companyNotes" sx={{ marginTop: 3 }}>
										Notes{' '}
										<CerebroText variant="hint" component="span">
											(Employee Only)
										</CerebroText>
									</CerebroLabel>
									{dealQuery.isSuccess ? (
										<CerebroRichText
											value={values.companyNotes ?? ''}
											onChange={updatedValue => setFieldValue('companyNotes', updatedValue)}
										/>
									) : (
										<Skeleton height={60} />
									)}
								</>
							)}
							<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
								Save &amp; Continue
							</CerebroPrimaryButton>
						</CerebroForm>
					)}
				</Formik>
			</CerebroPageContainer>

			<CerebroDrawer open={isBusinessDetailsExampleOpen} onClose={() => setIsBusinessDetailsExampleOpen(false)}>
				<CerebroHeading size="h3" gutterBottom>
					Example: What is the purpose of your financing? What is important to you about how the loan is
					structured?
				</CerebroHeading>
				<CerebroStyledBox sx={{ marginTop: 2 }}>
					<CerebroText gutterBottom>
						To further our recent growth, we are seeking up to $10MM ($5MM term loan and $5MM line of
						credit) of growth capital to invest in current product development (R&amp;D), expanded product
						offerings, and expanded sales capabilities across the US.
					</CerebroText>
					<CerebroText>
						Our priority for this financing is as follows: <br />
						1) Achieve the full $10 million loan size <br />
						2) Secure a term of at least 3 years for the revolver and 5 years for the term <br />
						3) Minimize cash interest payments. <br />
						4) Close within 75 days to meet our deadline.
					</CerebroText>
				</CerebroStyledBox>
			</CerebroDrawer>

			<CerebroDrawer open={isPrincipalDetailsExampleOpen} onClose={() => setIsPrincipalDetailsExampleOpen(false)}>
				<CerebroHeading size="h3" gutterBottom>
					Example: What is the company business model (how do you generate revenue)?
				</CerebroHeading>
				<CerebroStyledBox sx={{ marginTop: 2 }}>
					<CerebroText gutterBottom>
						We are a fast-growing cleaning products manufacturer supplying commercial-grade cleaning
						products to customers primarily in the healthcare and foodservice industries.
					</CerebroText>
					<CerebroText>
						We generate revenues by selling 50% to distributors and 50% through subscription to large
						fortune 500 companies. We have a 30% customer concentration with one company on subscription,
						but we are under a long-term contract.
					</CerebroText>
				</CerebroStyledBox>
			</CerebroDrawer>

			<CerebroDrawer
				open={isBusinessOwnershipNarrativeExampleOpen}
				onClose={() => setBusinessOwnershipNarrativeExampleOpen(false)}
			>
				<CerebroHeading size="h3" gutterBottom>
					Example: What is the ownership structure?
				</CerebroHeading>
				<CerebroStyledBox sx={{ marginTop: 2 }}>
					<CerebroText>
						The company is privately owned with one institutional investor (a PE Firm) owning about 15% of
						the company, 60% is owned by a passive founder, and 25% is owned by management team.
					</CerebroText>
				</CerebroStyledBox>
			</CerebroDrawer>
		</>
	);
};

const CompanyBusinessAndPrincipalSchema = Yup.object().shape({});

type DealMutationVariables = FormikUtils.MutateOnSubmitVariables<CompanyBusinessAndPrincipalFormValues>;
interface CompanyBusinessAndPrincipalFormValues {
	narrative: string;
	principalDetails: string;
	businessOwnershipNarrative: string;
	companyNotes: string;
}
